import { useMutation, UseMutationOptions } from '@tanstack/react-query';

import { validateAssignment } from '../../api';
import { ValidateAssignmentRequest, ValidateAssignmentResponse } from '../../types/dto';

/**
 * Hook for validating assignment overlap using React Query Mutation.
 * Checks if an assignment overlaps with existing assignments for the same user.
 *
 * @param options - Optional TanStack Query mutation options
 * @returns Mutation object with mutate, isPending, data, error
 *
 * @example
 * ```typescript
 * const validateMutation = useAssignmentValidation({
 *   onSuccess: (data) => {
 *     if (data.overlapping) {
 *       // Show warning
 *     } else {
 *       // Proceed with update
 *     }
 *   },
 *   onError: (error) => {
 *     // Handle validation error
 *   }
 * });
 *
 * // Trigger validation
 * validateMutation.mutate({
 *   userId: 1,
 *   projectId: 5,
 *   startDate: '2025-01-01T00:00:00Z',
 *   endDate: '2025-01-31T23:59:59Z',
 *   assignmentId: 10, // Optional: for edit validation
 * });
 * ```
 */
export function useAssignmentValidation(
  options?: UseMutationOptions<ValidateAssignmentResponse, Error, ValidateAssignmentRequest>
) {
  return useMutation({
    mutationFn: validateAssignment,
    ...options,
  });
}
